export const VENUE = { name: 'Legend Valley', latitude: 39.9378, longitude: -82.4067, timezone: 'America/New_York' };
export const WEATHER_LINKS = {
  forecast: 'https://forecast.weather.gov/MapClick.php?lat=39.9378&lon=-82.4067',
  radar: 'https://radar.weather.gov/station/KILN/standard',
  discussion: 'https://www.weather.gov/wrh/TextProduct?product=afdiln',
  festival: 'https://www.lostlandsfestival.com/exit/',
  met: 'https://api.met.no/doc/Locationforecast',
};

export interface WeatherSource<T> {
  status: 'ok' | 'unavailable';
  fetchedAt: string;
  issuedAt: string | null;
  data: T | null;
}
export interface WeatherAlert {
  id: string; event: string; headline: string; severity: string;
  sent: string; effective: string; expires: string; ends: string | null;
  description: string; instruction: string; url: string;
}
export interface WeatherHour {
  start: string; end: string; temperatureF: number | null;
  rainChance: number | null; rainMm: number | null;
  windMph: number | null; summary: string;
}
export interface WeatherObservation {
  station: string; time: string; temperatureF: number | null;
  rainLastHourMm: number | null; windMph: number | null; description: string;
}
export interface WeatherSnapshot {
  schema: 1; venue: typeof VENUE; fetchedAt: string;
  alerts: WeatherSource<WeatherAlert[]>;
  nws: WeatherSource<WeatherHour[]>;
  met: WeatherSource<WeatherHour[]>;
  observation: WeatherSource<WeatherObservation>;
}

export function isWeatherSnapshot(v: unknown): v is WeatherSnapshot {
  const s = obj(v);
  if (s.schema !== 1 || !date(s.fetchedAt) || obj(s.venue).latitude !== VENUE.latitude || obj(s.venue).longitude !== VENUE.longitude) return false;
  const nullableNumber = (n: unknown) => n === null || num(n) !== null;
  const hour = (v: unknown) => {
    const h = obj(v);
    return Boolean(date(h.start) && date(h.end) && Date.parse(str(h.end)) > Date.parse(str(h.start)) && typeof h.summary === 'string' && ['temperatureF','rainChance','rainMm','windMph'].every(k => nullableNumber(h[k])));
  };
  for (const key of ['alerts','nws','met','observation']) {
    const source = obj(s[key]);
    if (!date(source.fetchedAt) || !(source.issuedAt === null || date(source.issuedAt))) return false;
    if (source.status === 'unavailable') { if (source.data !== null) return false; continue; }
    if (source.status !== 'ok' || source.data === null) return false;
    if (key === 'alerts') {
      if (!Array.isArray(source.data) || !source.data.every(v => {
        const a = obj(v);
        return ['id','event','headline','severity','description','instruction'].every(k => typeof a[k] === 'string') && date(a.sent) && date(a.effective) && date(a.expires) && (a.ends === null || date(a.ends)) && (str(a.url).startsWith('https://api.weather.gov/alerts/') || a.url === WEATHER_LINKS.forecast);
      })) return false;
    } else if (key === 'observation') {
      const o = obj(source.data);
      if (!date(o.time) || typeof o.station !== 'string' || typeof o.description !== 'string' || !['temperatureF','rainLastHourMm','windMph'].every(k => nullableNumber(o[k]))) return false;
    } else if (!Array.isArray(source.data) || !source.data.length || !source.data.every(hour)) return false;
  }
  return true;
}

type Obj = Record<string, unknown>;
function obj(v: unknown): Obj { return v !== null && typeof v === 'object' ? v as Obj : {}; }
const str = (v: unknown): string => typeof v === 'string' ? v : '';
const num = (v: unknown): number | null => typeof v === 'number' && Number.isFinite(v) ? v : null;
const date = (v: unknown): string => Number.isFinite(Date.parse(str(v))) ? str(v) : '';
const value = (v: unknown): number | null => num(obj(v).value);
const fahrenheit = (v: unknown): number | null => num(v) === null ? null : Math.round((v as number) * 9 / 5 + 32);
const mph = (v: unknown): number | null => num(v) === null ? null : Math.round((v as number) * 2.23694);
export function ageMs(iso: string | null, now: number): number {
  const t = Date.parse(iso ?? '');
  return Number.isFinite(t) && t <= now + 60_000 ? Math.max(0, now - t) : Infinity;
}
export function freshSource(source: WeatherSource<unknown> | undefined, now: number, maxAge = 180_000): boolean {
  return source?.status === 'ok' && source.data !== null && ageMs(source.fetchedAt, now) <= maxAge;
}
export function activeAlerts(alerts: WeatherAlert[], now: number): WeatherAlert[] {
  return alerts.filter(a => Date.parse(a.expires) > now && (!a.ends || Date.parse(a.ends) > now));
}
export function weatherNoticeKey(alerts: WeatherAlert[], current: boolean): string {
  return JSON.stringify([current, alerts.map(a => [a.id, a.sent, a.expires, a.severity, a.headline]).sort((a, b) => a[0].localeCompare(b[0]))]);
}
export function parseAlerts(raw: unknown, now: number): WeatherAlert[] {
  const features = obj(raw).features;
  if (!Array.isArray(features)) throw new Error('Missing alert collection');
  const alerts = features.map(f => {
    const p = obj(obj(f).properties);
    if (!str(p.event) || !date(p.expires) || !date(p.effective) || !date(p.sent)) throw new Error('Incomplete alert');
    const id = str(obj(f).id) || str(p.id);
    return {
      id, event: str(p.event), headline: str(p.headline) || str(p.event), severity: str(p.severity),
      sent: date(p.sent), effective: date(p.effective), expires: date(p.expires), ends: date(p.ends) || null,
      description: str(p.description), instruction: str(p.instruction),
      url: id.startsWith('https://api.weather.gov/alerts/') ? id : WEATHER_LINKS.forecast,
    };
  });
  return activeAlerts(alerts, now).sort((a,b) => ['Extreme','Severe','Moderate','Minor','Unknown'].indexOf(a.severity) - ['Extreme','Severe','Moderate','Minor','Unknown'].indexOf(b.severity));
}
export function parseNws(raw: unknown): { hours: WeatherHour[]; issuedAt: string | null } {
  const p = obj(obj(raw).properties);
  if (!Array.isArray(p.periods) || !p.periods.length) throw new Error('Missing hourly forecast');
  const hours = p.periods.map(v => {
    const h = obj(v);
    if (!date(h.startTime) || !date(h.endTime) || Date.parse(str(h.endTime)) <= Date.parse(str(h.startTime))) throw new Error('Invalid forecast time');
    if (h.temperatureUnit !== 'C' && h.temperatureUnit !== 'F') throw new Error('Unexpected temperature unit');
    const probability = value(h.probabilityOfPrecipitation);
    if (probability !== null && (probability < 0 || probability > 100)) throw new Error('Invalid rain probability');
    const wind = str(h.windSpeed).match(/\d+(?:\.\d+)?/g)?.map(Number) ?? [];
    return { start: date(h.startTime), end: date(h.endTime),
      temperatureF: h.temperatureUnit === 'C' ? fahrenheit(h.temperature) : num(h.temperature),
      rainChance: probability, rainMm: null,
      windMph: /mph/.test(str(h.windSpeed)) && wind.length ? Math.max(...wind) : null,
      summary: str(h.shortForecast),
    };
  });
  return { hours: hours.slice(0,48), issuedAt: date(p.updateTime) || date(p.generatedAt) || null };
}
export function parseMet(raw: unknown): { hours: WeatherHour[]; issuedAt: string | null } {
  const p = obj(obj(raw).properties);
  if (!Array.isArray(p.timeseries) || !p.timeseries.length) throw new Error('Missing comparison forecast');
  const units = obj(obj(p.meta).units);
  if (units.air_temperature !== 'celsius' || units.wind_speed !== 'm/s' || units.precipitation_amount !== 'mm') throw new Error('Unexpected forecast units');
  const hours = p.timeseries.slice(0,48).map(v => {
    const t = obj(v); const data = obj(t.data); const instant = obj(obj(data.instant).details);
    if (!date(t.time)) throw new Error('Invalid comparison time');
    const next = obj(data.next_1_hours);
    return { start: date(t.time), end: new Date(Date.parse(date(t.time)) + 3_600_000).toISOString(),
      temperatureF: fahrenheit(instant.air_temperature), windMph: mph(instant.wind_speed),
      rainChance: null, rainMm: num(obj(next.details).precipitation_amount),
      summary: str(obj(next.summary).symbol_code).replace(/_/g, ' '),
    };
  });
  return { hours, issuedAt: date(obj(p.meta).updated_at) || null };
}
export function parseObservation(raw: unknown): WeatherObservation {
  const p = obj(obj(raw).properties);
  if (!date(p.timestamp)) throw new Error('Missing observation time');
  return { station: 'Newark–Heath (KVTA), off site', time: date(p.timestamp),
    temperatureF: obj(p.temperature).unitCode === 'wmoUnit:degC' ? fahrenheit(value(p.temperature)) : null,
    rainLastHourMm: obj(p.precipitationLastHour).unitCode === 'wmoUnit:mm' ? value(p.precipitationLastHour) : null,
    windMph: obj(p.windSpeed).unitCode === 'wmoUnit:km_h-1' && value(p.windSpeed) !== null ? Math.round(value(p.windSpeed)! / 1.609344) : null,
    description: str(p.textDescription) };
}
